import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { State as AppState } from 'reducers';
import { merge } from 'lodash';
import { BuildingId, DeviceClass, DeviceFamily, DeviceType, Theme } from 'shared/models';
import { Permission } from 'shared/permissions';
import { useDeviceTypes, useForm, useSession } from 'hooks';
import { SuccessMessage, ErrorMessage } from 'ui/shared/messages';
import { CheckField, TextField } from 'ui/shared/controls';
import { DeviceTypeSelectField } from '../deviceTypeSelectField/DeviceTypeSelectField';
import { CustomParametersField } from './customParametersTable/CustomParametersField';
import { Loading } from 'ui/shared/loading';
import { uploadThemeRequest, clearUploadTheme } from 'actions/themeActions';
import { isUploading, getUploadProgress, getUploadSuccess, getUploadError } from 'selectors/themesSelectors';
import { getBuilding, getBuildingDeviceTypes } from 'selectors/buildingsSelectors';
import { isGiottoOrRaffaelloDevice } from 'logic/isGiottoOrRaffaelloDevice';

export type Props = {
    isNew: boolean;
    isSubmitting: boolean;
    displayName?: string;
    width?: number;
    height?: number;
    requirements?: Theme['requirements'];
    options?: Theme['options'];
    buildingId: BuildingId;
    onAccept({ displayName, deviceType, width, height, uploaded, requirements, options }: any): void;
    onCancel(): void;
};

let currentDeviceType: DeviceType;
const getDeviceTypeDimensions = (deviceType: DeviceType, data: Partial<Theme>) => {
    if (currentDeviceType?.id === deviceType.id) {
        const shouldFlipDimensions = (data.width || 0) < (data.height || 0);
        return {
            width: data.width || (shouldFlipDimensions ? deviceType.pixelHeight! : deviceType.pixelWidth!),
            height: data.height || (shouldFlipDimensions ? deviceType.pixelWidth! : deviceType.pixelHeight!),
        };
    }
    else {
        currentDeviceType = { ...deviceType };
        
        return {
            width: deviceType.pixelWidth!,
            height: deviceType.pixelHeight!,
        };
    }
};

export const ThemePropertiesForm = ({ isNew, isSubmitting, displayName, width, height, requirements = {}, options = {}, buildingId, onAccept, onCancel }: Props) => {
    const { t } = useTranslation();

    const fileRef = useRef<HTMLInputElement>(null);
    const building = useSelector((state: AppState) => getBuilding(state, { buildingId }));

    const { hasPermission } = useSession();
    const { isLoadingDeviceTypes, deviceTypes } = useDeviceTypes();
    
    const isThemeUploading = useSelector((state: AppState) => isUploading(state));
    const themeUploadError = useSelector((state: AppState) => getUploadError(state));
    const themeUploadSuccess = useSelector((state: AppState) => getUploadSuccess(state));
    const themeUploadProgress = useSelector((state: AppState) => getUploadProgress(state));
 
    const buildingDeviceTypes = useSelector((state: AppState) => getBuildingDeviceTypes(state, { buildingId }));
    const defaultDeviceTypeIds = Object.keys(buildingDeviceTypes);

    const themeMemo = useMemo<Partial<Theme>>(() => ({
        displayName,
        width,
        height,
        requirements,
        options,
    }), []);

    const handleSave = useCallback(({ displayName, height, width, requirements, options }: Partial<Theme>) => {
        const deviceType = (deviceTypes && requirements?.deviceTypeId) ? deviceTypes[requirements?.deviceTypeId] : null;
        try {
            localStorage.setItem('theme-properties.width', `${width}`);
            localStorage.setItem('theme-properties.height', `${height}`);
        }
        catch { /* swallow error */ }
        onAccept({
            displayName,
            deviceType: (requirements?.deviceTypeId && deviceTypes[requirements.deviceTypeId]) || null,
            width,
            height,
            requirements: {
                ...requirements,
                pi: deviceType
                    ? (deviceType.deviceFamily === DeviceFamily.Giotto || requirements?.pi)
                    : requirements?.pi,
            },
            options,
            uploaded: false,
        });
    }, [ onAccept, deviceTypes ]);

    const { data, setData, handleChange, handleSubmit } = useForm('theme-properties', themeMemo, handleSave, { undefinedFalse: true });

    const dispatch = useDispatch();

    const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.currentTarget.files) {
            return;
        }
        const file = e.currentTarget.files[0];
        const { requirements, options } = data;
        dispatch(uploadThemeRequest({
            buildingId,
            displayName: data.displayName || file.name,
            width: data.width,
            height: data.height,
            file,
            requirements,
            options,
        }));
    };

    const handleClickError = () => dispatch(clearUploadTheme());

    const handleClickUpload = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        fileRef.current!.click();
    };

    const deviceType = data.requirements?.deviceTypeId ? deviceTypes[data.requirements.deviceTypeId] : undefined;
    const canUpload =
        deviceType?.deviceFamily === DeviceFamily.MatisseR4
        || deviceType?.deviceFamily === DeviceFamily.TTG
        || deviceType?.deviceFamily === DeviceFamily.MatisseFlexR2
    ;

    useEffect(() => {
        if (deviceType) {
            const { deviceCapabilities = {} } = deviceType;
            const isMatisse = deviceCapabilities["Matisse"];
            const isTtg = deviceCapabilities["TTG"];
            setData(data => {
                // Set legacy device class flags
                const requirements = merge({}, data.requirements);
                const options = merge({}, data.options);
                const { width, height } = getDeviceTypeDimensions(deviceType, data);

                if (isMatisse) {
                    requirements.Matisse = true;
                }
                else {
                    delete requirements.Matisse;
                }

                if (isTtg) {
                    requirements.TTG = true;
                }
                else {
                    delete requirements.TTG;
                }

                return {
                    ...data,
                    width,
                    height,
                    requirements,
                    options,
                };
            });
        }
    }, [ data.requirements?.deviceTypeId, setData, deviceType ]);

    useEffect(() => {
        setData(data => {
            const requirements = merge({}, data.requirements);
            if (building?.hasEcs && !isGiottoOrRaffaelloDevice(deviceType)) {
                requirements.ecs = true;
            }
            else {
                delete requirements.ecs;
            }
            return {
                ...data,
                requirements,
            };
        });
    }, [ building?.hasEcs, setData, deviceType ]);

    if (isLoadingDeviceTypes) {
        return <Loading/>;
    }
    
    return (
        <div style={ { minWidth: '360px', maxWidth: '400px' } }>
            <form
                id="theme-properties-form"
                onSubmit={ handleSubmit }
                className={ `ui form${(isSubmitting || (isThemeUploading && !themeUploadError && !themeUploadSuccess)) ? ' loading' : ''}` }
            >
                <div className="ui grid">
                    <div className="row">
                        <div className="sixteen wide column">
                            <TextField
                                value={ data.displayName || '' }
                                onChange={ handleChange }
                                id="theme-properties-name"
                                name="displayName"
                                type="text"
                                labelKey="THEME_EDITOR.LABELS.NAME"
                                maxLength={ 32 }
                                autoFocus={ true }
                                required={ true }
                                placeholder={ t("THEMES.TITLE_PLACEHOLDER") }
                            />
                        </div>
                    </div>
                    { (isNew || hasPermission(null, Permission.AdminTheme)) &&
                        
                        <div className="row">
                            <div className="ten wide column">
                                <DeviceTypeSelectField
                                    id="theme-properties-device-type"
                                    name="requirements.deviceTypeId"
                                    deviceClass={ DeviceClass.Display }
                                    value={ data.requirements?.deviceTypeId }
                                    onChange={ handleChange }
                                    initialSelection={ defaultDeviceTypeIds }
                                />
                            </div>
                            <div className="six wide column" style={ { paddingTop: '1.65em' } }>
                                <CheckField
                                    id="theme-properties-pi"
                                    labelKey="THEME_EDITOR.LABELS.PI"
                                    readOnly={ deviceType?.deviceFamily === DeviceFamily.Giotto }
                                    input={ {
                                        name: 'requirements.pi',
                                        checked: !!(deviceType?.deviceFamily === DeviceFamily.Giotto || data.requirements?.pi),
                                        onChange: handleChange,
                                    } }
                                />
                            </div>
                        </div>
                    }
                    <div className="row">
                        <div className="five wide column">
                            <TextField
                                id="theme-properties-width"
                                name="width"
                                type="number"
                                min={ 0 }
                                max={ 8192 }
                                labelKey="THEME_EDITOR.LABELS.WIDTH"
                                onChange={ handleChange }
                                value={ data.width || '' }
                                required={ true }
                            />
                        </div>
                        <div className="five wide column">
                            <TextField
                                id="theme-properties-height"
                                name="height"
                                type="number"
                                min={ 0 }
                                max={ 8192 }
                                labelKey="THEME_EDITOR.LABELS.HEIGHT"
                                onChange={ handleChange }
                                value={ data.height || '' }
                                required={ true }
                            />
                        </div>
                        <div className="six wide column" style={ { position: 'relative' } }>
                            <button
                                type="button"
                                className="ui button"
                                onClick={ () => setData({ ...data, width: data.height, height: data.width }) }
                                style={ { position: 'absolute', bottom: '1px' } }>
                                { t('COMMAND.ROTATE') }
                            </button>
                        </div>
                    </div>
                    { !isNew && (
                        <div className="row">
                            <CustomParametersField
                                theme={ data }
                                setData={ setData }
                                onChange={ handleChange }
                            />
                        </div>
                    )}
                    { !isNew && hasPermission(null, Permission.Admin) &&
                        
                        <div className="row">
                            <div className="sixteen wide column">
                                <CheckField
                                    id="theme-properties-disableMinTextHeight"
                                    labelKey="THEME_EDITOR.LABELS.DISABLE_MIN_TEXT_HEIGHT"
                                    input={ {
                                        name: 'requirements.disableMinTextHeight',
                                        checked: !!data.requirements?.disableMinTextHeight,
                                        onChange: handleChange,
                                    } }
                                />
                            </div>
                        </div>
                    }
                </div>

                <div style={ { marginTop: '3em' } }>
                    <button
                        className="ui primary submit button"
                        type="submit"
                        disabled={ isNew && (
                            isThemeUploading
                            || !data.requirements?.deviceTypeId
                            || !data.width
                            || !data.height
                            || !data.displayName
                        ) }
                    >
                        { t(isNew ? 'COMMAND.CREATE_THEME' : 'COMMAND.UPDATE') }
                    </button>
                    { isNew && canUpload && hasPermission(null, Permission.UploadTheme) &&
                        <button className="ui button" type="button" disabled={ isThemeUploading && !themeUploadError } onClick={ handleClickUpload }>
                            { t('COMMAND.UPLOAD') }
                        </button>
                    }
                    <button className="ui button" type="button" disabled={ isThemeUploading && !themeUploadError } onClick={ onCancel }>
                        { t('COMMAND.CANCEL') }
                    </button>
                </div>

                { isNew && canUpload && hasPermission(null, Permission.UploadTheme) &&
                    <input
                        type="file"
                        ref={ fileRef }
                        onChange={ handleSelectFile }
                        style={ {
                            visibility: 'hidden',
                            opacity: 0,
                            lineHeight: '0',
                            position: 'absolute',
                            pointerEvents: 'none',
                        } }
                        accept={ getUploadAcceptString(deviceType) }
                    />
                }
            </form>

            { isThemeUploading && !themeUploadSuccess && !themeUploadError &&
                <progress
                    max="100"
                    value={ `${themeUploadProgress || 0 * 100}` }
                    style={ { marginTop: '2em', width: '100%' } }
                />
            }

            { themeUploadSuccess &&
                <SuccessMessage message={ t('THEMES.NEW_DIALOG.UPLOAD_SUCCESS') }/>
            }

            { themeUploadError &&
                <ErrorMessage
                    message={ themeUploadError }
                    onClick={ handleClickError }
                />
            }
        </div>
    );
};

function getUploadAcceptString(deviceType: DeviceType | null): string {
    switch (deviceType?.deviceFamily) {
        case DeviceFamily.TTG:
            return '.ttg';
        case DeviceFamily.MatisseR4:
            return '.tar,.mpack';
        case DeviceFamily.MatisseR5:
            return '.mpack,.mr5';
        default:
            return '';
    }
}
